import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPost } from "../../services/api";

export default function RefundTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [lookupId, setLookupId] = useState("");
  const [transaction, setTransaction] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [success, setSuccess] = useState("");

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookupId.trim()) return;
    setError("");
    setTransaction(null);
    setSuccess("");
    setLoading(true);

    try {
      const txn = await apiGet<any>(`/organizations/${orgId}/transactions/${lookupId.trim()}`);
      setTransaction(txn);
    } catch {
      setError("Transaction not found. Check the receipt number and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!transaction) return;
    if (!confirm(`Refund $${(transaction.total || 0).toFixed(2)} to customer?`)) return;
    setRefunding(true);

    try {
      // Create a refund transaction (negative amount)
      await apiPost(`/organizations/${orgId}/transactions`, {
        locationId: transaction.locationId,
        employeeId: user?.id || "",
        items: transaction.items.map((i: any) => ({ ...i, price: -i.price })),
        subtotal: -(transaction.subtotal || 0),
        tax: -(transaction.tax || 0),
        total: -(transaction.total || 0),
        paymentMethod: transaction.paymentMethod,
        status: "refunded",
        refundedTransactionId: transaction.transactionId,
        refundedReceiptNumber: transaction.receiptNumber,
      });

      // Mark original as refunded
      const { apiPut } = await import("../../services/api");
      await apiPut(`/organizations/${orgId}/transactions/${transaction.transactionId}`, {
        ...transaction,
        status: "refunded",
      });

      setSuccess(`Refund of $${(transaction.total || 0).toFixed(2)} processed successfully.`);
      setTransaction(null);
      setLookupId("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Refund failed");
    } finally {
      setRefunding(false);
    }
  };

  return (
    <div>
      <div className="settings-section">
        <h2>Process Refund</h2>
        <p className="section-desc">
          Enter or scan the receipt number to look up a transaction and issue a refund.
        </p>

        {error && <div className="error-message" style={{ marginBottom: "1rem" }}>{error}</div>}
        {success && <div style={{ background: "#dcfce7", border: "1px solid #86efac", color: "#166534", padding: "0.65rem 0.85rem", borderRadius: "6px", fontSize: "0.85rem", marginBottom: "1rem" }}>{success}</div>}

        <form onSubmit={handleLookup}>
          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.5rem" }}>
            <input
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value.toUpperCase())}
              placeholder="Enter receipt # (e.g. A3K7WN2P)"
              style={{ flex: 1, padding: "0.7rem 0.85rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "1rem", fontFamily: "monospace", letterSpacing: "1px" }}
              autoFocus
            />
            <button type="submit" className="btn btn-primary" style={{ width: "auto" }} disabled={loading || !lookupId.trim()}>
              {loading ? "Looking up..." : "Look Up"}
            </button>
          </div>
        </form>

        {transaction && (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "1.25rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: "1rem" }}>Receipt #{transaction.receiptNumber || transaction.transactionId?.slice(0, 8)}</div>
                <div style={{ color: "#6b7280", fontSize: "0.85rem" }}>{new Date(transaction.createdAt).toLocaleString()}</div>
              </div>
              <div>
                <span style={{
                  padding: "0.2rem 0.6rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 500,
                  background: transaction.status === "refunded" ? "#fef2f2" : "#dcfce7",
                  color: transaction.status === "refunded" ? "#dc2626" : "#166534",
                }}>
                  {transaction.status}
                </span>
              </div>
            </div>

            <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: "0.75rem" }}>
              {(transaction.items || []).map((item: any, i: number) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.25rem 0", fontSize: "0.9rem" }}>
                  <span>{item.quantity}× {item.name}</span>
                  <span>${(Math.abs(item.price) * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "0.75rem", paddingTop: "0.75rem", display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "1.1rem" }}>
              <span>Total</span>
              <span>${(Math.abs(transaction.total) || 0).toFixed(2)}</span>
            </div>

            <div style={{ color: "#6b7280", fontSize: "0.85rem", marginTop: "0.5rem" }}>
              Payment: {transaction.paymentMethod}
            </div>

            {transaction.status === "completed" && (
              <button
                onClick={handleRefund}
                disabled={refunding}
                style={{ marginTop: "1rem", width: "100%", padding: "0.75rem", background: "#dc2626", color: "white", border: "none", borderRadius: "6px", fontSize: "0.95rem", fontWeight: 600, cursor: "pointer" }}
              >
                {refunding ? "Processing Refund..." : `Refund $${(transaction.total || 0).toFixed(2)}`}
              </button>
            )}

            {transaction.status === "refunded" && (
              <div style={{ marginTop: "1rem", textAlign: "center", color: "#dc2626", fontWeight: 500 }}>
                This transaction has already been refunded.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
